// src/components/EmployeeAnnouncements.jsx
import React, { useEffect, useMemo, useState } from "react";
import "./EmployeeAnnouncements.css";

export default function EmployeeAnnouncements({ employee }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [activeRow, setActiveRow] = useState(null);

  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  const empName = (employee?.employeeName || employee?.name || "").toLowerCase();
  const dept = (employee?.department || "").toLowerCase();

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetch("https://hrms.urbango.co.in/server/index.php?endpoint=announce_list")
      .then((r) => r.json())
      .then((data) => {
        if (!alive) return;
        const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
        setRows(list);
        setLoading(false);
      })
      .catch(() => {
        if (!alive) return;
        setErr("Unable to load announcements.");
        setLoading(false);
      });
    return () => { alive = false; };
  }, []);

  // reset to first page if dataset length changes
  useEffect(() => { setPage(1); }, [rows.length]);

  const visible = useMemo(() => {
    const norm = (x) => String(x || "").trim().toLowerCase();
    return rows
      .filter((r) => {
        const targetDept = norm(r.Department || r.department);
        const targetEmpName = norm(r.EmployeeName || r.employeeName);
        const allowDept = !dept || targetDept === "all" || targetDept === dept;
        const allowEmp = !empName || targetEmpName === "all" || targetEmpName === empName;
        return allowDept && allowEmp;
      })
      .sort((a, b) => new Date(b.Date || b.date || 0) - new Date(a.Date || a.date || 0));
  }, [rows, empName, dept]);

  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE;
  const current = visible.slice(start, start + PAGE_SIZE);

  const openModal = (row) => { setActiveRow(row); setModalOpen(true); };

  if (loading) return <div className="notif-wrap"><div className="notif-card">Loading announcements…</div></div>;
  if (err) return <div className="notif-wrap"><div className="notif-card">{err}</div></div>;

  return (
    <div className="notif-wrap">
      <div className="notif-card">
        <div className="notif-title">NOTIFICATIONS</div>

        {visible.length === 0 ? (
          <div className="notif-empty">No announcements yet.</div>
        ) : (
          <ul className="notif-list">
            {current.map((r, idx) => {
              const title = r.Title || r.title || "";
              const deptText = r.Department || r.department || "all";
              const dateObj = new Date(r.Date || r.date || Date.now());
              const datePretty = isNaN(dateObj) ? (r.Date || r.date || "") :
                dateObj.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

              const body = (r.Messages || r.messages || "").trim();
              const desc = body.split(/\n|\. /)[0].slice(0, 120) + (body.length > 120 ? "…" : "");

              return (
                <li className="notif-item" key={`${title}-${idx}`} onClick={() => openModal(r)}>
                  <div className="notif-left">
                    <span className="notif-dot" />
                    <div className="notif-main">
                      <div className="notif-row1">
                        <span className="notif-head">{title}</span>
                      </div>
                      <div className="notif-desc">{desc}</div>
                      <div className="notif-author">{deptText === "all" ? "All Departments" : deptText}</div>
                    </div>
                  </div>
                  <div className="notif-right">
                    <span className="notif-tag">{deptText}</span>
                    <span className="notif-date">{datePretty}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {visible.length > 0 && (
          <div className="notif-pager">
            <button className="notif-btn" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
            <span className="notif-page">{page}/{totalPages}</span>
            <button className="notif-btn" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</button>
          </div>
        )}
      </div>

      {modalOpen && activeRow && (
        <>
          <div className="ea-modal-backdrop" onClick={() => setModalOpen(false)} />
          <div className="ea-modal" role="dialog" aria-modal="true">
            <div className="ea-modal-card">
              <div className="ea-modal-headline">
                <div className="ea-modal-subject">
                  <h3>{activeRow.Title}</h3>
                  <span className="ea-badge-dept-lg">{activeRow.Department || "all"}</span>
                </div>
                <div className="ea-modal-date-lg">
                  {new Date(activeRow.Date || activeRow.date).toLocaleDateString("en-IN", {
                    day: "2-digit", month: "short", year: "numeric",
                  })}
                </div>
              </div>

              <div className="ea-modal-fields">
                <div className="ea-field">
                  <span className="ea-label">Date:</span>
                  <span className="ea-value">
                    {new Date(activeRow.Date || activeRow.date).toLocaleDateString("en-IN", {
                      day: "2-digit", month: "short", year: "numeric",
                    })}
                  </span>
                </div>
                <div className="ea-field">
                  <span className="ea-label">Title:</span>
                  <span className="ea-value">{activeRow.Title}</span>
                </div>
                <div className="ea-field">
                  <span className="ea-label">Department:</span>
                  <span className="ea-value">{activeRow.Department || "all"}</span>
                </div>
                <div className="ea-field">
                  <span className="ea-label">Body:</span>
                  <span className="ea-value"><pre>{activeRow.Messages}</pre></span>
                </div>
              </div>

              <div className="ea-modal-actions">
                <button className="ea-btn-lite" onClick={() => setModalOpen(false)}>Close</button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
